/**
 * Generic responsive role shell — desktop sidebar + top bar; app-like bottom nav on mobile.
 * Shared by the patient, doctor (and future staff) surfaces. Pass a title + nav items.
 */

import type { ReactNode } from "react";
import { Outlet } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { AppShell, ShellSidebar, ShellTabBar } from "./AppShell";

export interface RoleNavItem {
  to: string;
  label: string;
  short: string;
  icon: ReactNode;
  /** Other paths whose pages belong to this entry (see ShellNavItem). */
  also?: string[];
}

export function RoleLayout({
  title,
  nav,
  profile,
  actions,
}: {
  title: string;
  nav: RoleNavItem[];
  /** Account panel opened from the name top right (AppShell `profile`). */
  profile?: ReactNode;
  /** Header controls before the account (AppShell `actions`). */
  actions?: ReactNode;
}) {
  const { user, logout } = useAuth();

  return (
    <AppShell
      title={title}
      userName={user?.full_name || title}
      onSignOut={() => void logout()}
      // No `end`: a list's entry stays lit on its detail pages, e.g. one prescription (RESP-04).
      sidebar={<ShellSidebar nav={nav} />}
      tabBar={<ShellTabBar nav={nav} />}
      profile={profile}
      actions={actions}
    >
      <Outlet />
    </AppShell>
  );
}

/* shared small icons */
function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-full w-full">
      {children}
    </svg>
  );
}
export const RxIcon = () => (
  <Svg>
    <path d="M5 21V7a2 2 0 0 1 2-2h6l4 4v3" />
    <path d="M13 3v6h6" />
    <path d="M14 14l5 5m0-5l-5 5" />
  </Svg>
);
export const UsersIcon = () => (
  <Svg>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 19a6 6 0 0 1 12 0" />
    <path d="M16 6a3 3 0 0 1 0 6m5 7a5 5 0 0 0-4-5" />
  </Svg>
);
export const PinIcon = () => (
  <Svg>
    <path d="M12 21s7-6.5 7-11a7 7 0 1 0-14 0c0 4.5 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </Svg>
);
export const UploadIcon = () => (
  <Svg>
    <path d="M12 16V4m0 0l-4 4m4-4l4 4" />
    <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </Svg>
);
export const UserIcon = () => (
  <Svg>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </Svg>
);
export const ListIcon = () => (
  <Svg>
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </Svg>
);
export const PlusIcon = () => (
  <Svg>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);
export const BagIcon = () => (
  <Svg>
    <path d="M6 7h12l1 13H5L6 7Z" />
    <path d="M9 7a3 3 0 0 1 6 0" />
  </Svg>
);

export const BellIcon = () => (
  <Svg>
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </Svg>
);

export const GearIcon = () => (
  <Svg>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10 1.4 1.4m0-12.8-1.4 1.4m-10 10-1.4 1.4" />
  </Svg>
);

export const LifebuoyIcon = () => (
  <Svg>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="m5 5 3.5 3.5m7 7L19 19M19 5l-3.5 3.5m-7 7L5 19" />
  </Svg>
);

export const AlarmIcon = () => (
  <Svg>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2.5 2.5M5 3 2 6m17-3 3 3" />
  </Svg>
);

export const SosIcon = () => (
  <Svg>
    <path d="M12 21s-7-4.4-9.3-9A5.3 5.3 0 0 1 12 6.3 5.3 5.3 0 0 1 21.3 12C19 16.6 12 21 12 21Z" />
    <path d="M12 10v3m-1.5-1.5h3" />
  </Svg>
);
